// Z92: last look before a declaration leaves for Descartes. Shows what is actually about to be sent
// (VAT treatment, file, environment) and requires an explicit tick. Extended later with the
// cross-check section (declared vs job vs CI) -- see the review-dialog spec.
import { useState } from 'react'
import type { PvaMode, ReviewItem, PrecheckCheck, VatAdjustmentInfo, EcustomsGenerateV2Result } from '../hooks/useECustoms'

interface Props {
  jobRef: string
  importerName: string | null
  pvaMode: PvaMode
  // PR-C: the final values of every checklist item, READ FROM THE FILE that is about to be sent
  decisions: EcustomsGenerateV2Result['decisions'] | null
  review: ReviewItem[]
  checks: PrecheckCheck[]
  vatAdjustment: VatAdjustmentInfo | null
  filename: string
  environment: 'UAT' | 'PROD'
  submitting: boolean
  onConfirm: () => void
  onCancel: () => void
}

export default function SubmitConfirmDialog({ jobRef, importerName, pvaMode, decisions, review, checks, vatAdjustment, filename, environment, submitting, onConfirm, onCancel }: Props) {
  const [reviewed, setReviewed] = useState(false)
  const prod = environment === 'PROD'
  const row = 'flex justify-between gap-4 py-1.5 border-b border-gray-100 dark:border-gray-700 text-sm'
  // The 13 checklist items, final values: 5 decisions + arrival id + the review items read from the CSV.
  const decisionLines: [string, string][] = decisions ? [
    ['Declaration Type', decisions.declType === 'IMD' ? 'IMD (goods not yet arrived)' : 'IMA (goods arrived)'],
    ['Declarant Representation', decisions.declarantRep === '2' ? '2 (direct)' : '3 (indirect)'],
    ['VAT treatment', decisions.pvaMode === 'PVA' ? 'PVA' : 'Non-PVA'],
    ['Duty & VAT payment', decisions.danMode === 'DAN' ? 'Via CY UK DAN' : 'No DAN'],
    ['Load type', `${decisions.loadType}${vatAdjustment?.applicable ? ` -- VAT Adjustment ${vatAdjustment.amount.toFixed(2)} GBP${vatAdjustment.manual ? ' (edited by hand)' : ''}` : ''}`],
    ['Arrival Transport ID', decisions.arrivalId],
  ] : []
  const lines: [string, string][] = [...decisionLines, ...review.map((r): [string, string] => [r.label, r.value || '—'])]
  const reds = checks.filter(c => c.level === 'red'), yellows = checks.filter(c => c.level === 'yellow')
  const asText = [`Descartes ${environment} -- ${jobRef} -- ${filename}`, ...lines.map(([k, v]) => `${k}: ${v}`), ...yellows.map(c => `WARNING: ${c.message}`)].join('\n')
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4" role="dialog" aria-modal="true">
      <div className="max-h-[90vh] w-full max-w-lg overflow-y-auto rounded-xl bg-white shadow-xl dark:bg-gray-800">
        <div className={`rounded-t-xl px-6 py-4 ${prod ? 'bg-red-600 text-white' : 'border-b border-gray-200 dark:border-gray-700'}`}>
          <h2 className="text-lg font-semibold">Submit to Descartes {environment}</h2>
          {prod && <p className="text-sm font-medium">PRODUCTION -- this goes to HMRC. A wrong declaration has to be corrected with them.</p>}
        </div>
        <div className="px-6 py-4">
          <div className={row}><span className="text-gray-500">Environment</span>
            <span className={`rounded px-2 py-0.5 font-bold ${prod ? 'bg-red-600 text-white' : 'bg-blue-100 text-blue-800 dark:bg-blue-900/40 dark:text-blue-200'}`}>{environment}</span></div>
          <div className={row}><span className="text-gray-500">VAT treatment</span>
            <span className="rounded bg-amber-100 px-2 py-0.5 font-bold text-amber-900 dark:bg-amber-900/40 dark:text-amber-200">{pvaMode === 'PVA' ? 'PVA (postponed VAT accounting)' : 'Non-PVA'}</span></div>
          <div className={row}><span className="text-gray-500">File</span><span className="font-mono">{filename}</span></div>
          <div className={row}><span className="text-gray-500">Job</span><span className="font-mono">{jobRef}</span></div>
          <div className={row}><span className="text-gray-500">Importer</span><span>{importerName ?? '—'}</span></div>
          {lines.map(([k, v]) => (
            <div key={k} className={row}><span className="text-gray-500">{k}</span><span className="text-right font-mono">{v}</span></div>
          ))}
          {(reds.length > 0 || yellows.length > 0) && (
            <div className="mt-3 space-y-1">
              {reds.map(c => <p key={c.id} className="rounded bg-red-50 p-2 text-sm text-red-800 dark:bg-red-900/20 dark:text-red-200">✕ {c.message}</p>)}
              {yellows.map(c => <p key={c.id} className="rounded bg-yellow-50 p-2 text-sm text-yellow-900 dark:bg-yellow-900/20 dark:text-yellow-100">⚠ {c.message}</p>)}
            </div>
          )}
          <button type="button" className="mt-2 text-xs text-blue-600 underline" onClick={() => navigator.clipboard?.writeText(asText)}>Copy as text</button>
          <label className="mt-4 flex items-start gap-2 text-sm text-gray-800 dark:text-gray-200">
            <input type="checkbox" className="mt-1" checked={reviewed} onChange={e => setReviewed(e.target.checked)} />
            <span>I have reviewed every value above against the file that will be sent. The VAT treatment ({pvaMode === 'PVA' ? 'PVA' : 'Non-PVA'}) and the duty payment choice are correct for this importer.</span>
          </label>
        </div>
        <div className="flex justify-end gap-2 border-t border-gray-200 px-6 py-3 dark:border-gray-700">
          <button onClick={onCancel} disabled={submitting} className="rounded-lg border border-gray-300 px-4 py-2 text-sm text-gray-700 dark:border-gray-600 dark:text-gray-200">Cancel</button>
          <button onClick={onConfirm} disabled={!reviewed || submitting}
            className={`rounded-lg px-4 py-2 text-sm font-medium text-white disabled:opacity-50 ${prod ? 'bg-red-600 hover:bg-red-700' : 'bg-blue-600 hover:bg-blue-700'}`}>
            {submitting ? 'Submitting…' : `Submit to ${environment}`}
          </button>
        </div>
      </div>
    </div>
  )
}
